import { type ReactNode, useRef, memo } from 'react';
import { styled, TableCell as MuiTableCell } from '@mui/material';
import { getCommonPinningStyles } from './tableFunctions';
import type { DataSchema, Schema } from 'types';
import type { Cell, Row } from '@tanstack/react-table';

const StyledTextAreaTableCell = styled(MuiTableCell)(() => ({
  paddingTop: '8px',
  paddingBottom: '0px',
}));

interface TableCellHeliosResizableTextAreaProps<T extends keyof P, P extends DataSchema> {
  cell: Cell<Schema<T, P>, unknown>;
  row: Row<Schema<T, P>>;
  entireRowClicked?: (row: Schema<T, P>) => void;
  renderCellContent: (textAreaMode?: boolean) => string | number | boolean | Iterable<ReactNode> | JSX.Element | null | undefined;
}

/** entireRowClicked is NOT called during TextArea resize */
const TableCellHeliosResizableTextAreaBase = <T extends keyof P, P extends DataSchema>({
  cell,
  entireRowClicked,
  row,
  renderCellContent,
}: TableCellHeliosResizableTextAreaProps<T, P>) => {
  const textAreaSizeBeforeInteractionRef = useRef<{ width: number; height: number } | null>(null);
  const suppressNextRowClickRef = useRef(false);

  const handleTextAreaMouseDown = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) {
      textAreaSizeBeforeInteractionRef.current = null;
      return;
    }

    const textAreaElement = e.currentTarget.querySelector('textarea');
    if (!textAreaElement || !textAreaElement.contains(target)) {
      textAreaSizeBeforeInteractionRef.current = null;
      return;
    }

    textAreaSizeBeforeInteractionRef.current = {
      width: textAreaElement.offsetWidth,
      height: textAreaElement.offsetHeight,
    };
  };

  const handleTextAreaMouseUp = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
    const sizeBeforeInteraction = textAreaSizeBeforeInteractionRef.current;
    if (!sizeBeforeInteraction) {
      return;
    }

    const textAreaElement = e.currentTarget.querySelector('textarea');
    if (!textAreaElement) {
      textAreaSizeBeforeInteractionRef.current = null;
      return;
    }

    const hasResized = textAreaElement.offsetWidth !== sizeBeforeInteraction.width || textAreaElement.offsetHeight !== sizeBeforeInteraction.height;

    suppressNextRowClickRef.current = hasResized;
    textAreaSizeBeforeInteractionRef.current = null;
  };

  return (
    <StyledTextAreaTableCell
      key={cell.id}
      sx={getCommonPinningStyles(cell.column, { id: cell.row.id })}
      onMouseDown={handleTextAreaMouseDown}
      onMouseUp={handleTextAreaMouseUp}
      onClick={(e) => {
        e.stopPropagation();

        if (suppressNextRowClickRef.current) {
          suppressNextRowClickRef.current = false;
          return;
        }

        entireRowClicked?.(row.original);
      }}>
      {renderCellContent(true)}
    </StyledTextAreaTableCell>
  );
};

export const TableCellHeliosResizableTextArea = memo(TableCellHeliosResizableTextAreaBase) as typeof TableCellHeliosResizableTextAreaBase;
